/**
 * MessageCard — the single card used for error / no-data / invalid-
 * layout states across the render layer (uniform-grid review #12).
 *
 * Replaces the hand-rolled `rounded-xl border … p-4` divs that were
 * duplicated (with subtle drift: `p-4` vs ChartCard's `px-5 pt-4`)
 * across GenericChart's error + no-data states and DashboardBody. The
 * chrome intentionally matches `ChartCard`
 * (`bi-components/src/lib/shared.jsx`): px-5 header with a text-sm
 * bold truncated title, so a message card sitting next to real chart
 * cards reads as the same family.
 *
 * `fill` mirrors the ChartCard fill contract: the card stretches to
 * its fixed grid cell (`h-full`) and clips overflow.
 */

import type { ReactNode } from "react";

export interface MessageCardProps {
  /** Card title (same slot as a chart card's title). Optional — the
   *  layout-error card has no natural title. */
  title?: string;
  /** The message body. */
  children: ReactNode;
  /** Uniform-grid mode: stretch to the grid cell. */
  fill?: boolean;
  /** `muted` (default) for empty states, `error` for failures. */
  tone?: "muted" | "error";
  /** This card is a transient LOADING state (F7-R2 #1/#5): stamps
   *  `data-pm-loading` so the server-side capture's quiescence check
   *  refuses to screenshot while it is on screen. Board tiles use it
   *  for their text-only "Loading…" cards. */
  busy?: boolean;
}

export function MessageCard({
  title,
  children,
  fill = false,
  tone = "muted",
  busy = false,
}: MessageCardProps): JSX.Element {
  const isError = tone === "error";
  return (
    <div
      role={isError ? "alert" : undefined}
      data-pm-loading={busy ? "" : undefined}
      className={`rounded-xl border shadow-sm${
        isError ? " border-red-200 bg-red-50/50" : " border-neutral-200 bg-white"
      }${fill ? " h-full overflow-hidden" : ""}`}
    >
      {title != null && title !== "" && (
        <div className="px-5 pt-4 pb-2">
          <h3 className="text-sm font-bold text-neutral-800 truncate">{title}</h3>
        </div>
      )}
      <div className={title != null && title !== "" ? "px-5 pb-4" : "px-5 py-4"}>
        <p className={`text-sm ${isError ? "text-negative" : "text-neutral-500"}`}>
          {children}
        </p>
      </div>
    </div>
  );
}
